import ExcelJS from 'exceljs';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const EXCEL_SHEET_NAME = 'Resultados';
const PDF_PAGE_FORMAT = 'a4';
const PDF_FONT_SIZE = 8;
const PDF_HEADER_FONT_SIZE = 8;
const PDF_MIN_CELL_WIDTH = 78;
const PDF_MAX_CELL_WIDTH = 156;
const PDF_MAX_HEADER_LINES = 3;
const PDF_MAX_BODY_LINES = 8;
const PDF_MARGINS = { top: 54, left: 30, right: 30, bottom: 30 };
const PDF_ROWS_PER_TABLE = 120;
const YIELD_EVERY_ROWS = 200;

const sanitizeFilenamePart = (value) =>
  String(value || 'encuesta')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split('')
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code >= 32 && !/[<>:"/\\|?*]/.test(char);
    })
    .join('')
    .replace(/\s+/g, '_')
    .slice(0, 80);

export const getSurveyFilename = (poll, extension) =>
  `${sanitizeFilenamePart(poll?.title || 'encuesta')}_usuarios.${extension}`;

const yieldToBrowser = () =>
  new Promise((resolve) => {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => resolve());
      return;
    }
    setTimeout(resolve, 0);
  });

export const ageTypeLabel = (ageType) =>
  ageType === 0 ? 'Exento'
  : ageType === 1 ? 'Infantil'
  : ageType === 2 ? 'Juvenil'
  : ageType === 3 ? 'Adulto'
  : 'Desconocido';

export const formatCurrency = (amountCents) =>
  `${(Number(amountCents || 0) / 100).toFixed(2)} EUR`;

export const paymentMethodLabel = (paymentMethod) =>
  paymentMethod === 'bizum' ? 'Bizum'
  : paymentMethod === 'manual' ? 'Casal'
  : paymentMethod === 'online' ? 'Tarjeta'
  : paymentMethod === 'free' ? 'Gratis'
  : '-';

export const paymentStatusLabel = (status) =>
  status === 'paid' ? 'Pagado online'
  : status === 'free' ? 'Gratis'
  : status === 'pending_manual' ? 'Pago en casal'
  : status === 'pending_online' ? 'Pendiente de pago'
  : status === 'unpaid' ? 'Sin pagar'
  : status === 'cancelled' ? 'Cancelado'
  : '-';

export const paymentConceptLabel = (row, fallback = '-') => {
  const items = Array.isArray(row?.paidItems) ? row.paidItems : [];
  if (items.length) {
    return items.map((item) => `${item.label} ${formatCurrency(item.amountCents)}`).join(', ');
  }
  return row?.conceptLabel || fallback;
};

export const getAnswerText = (question, answerIds) => {
  if (!answerIds || (Array.isArray(answerIds) && answerIds.length === 0)) return '';

  if (question?.type === 'text') {
    return (Array.isArray(answerIds) ? answerIds : [answerIds])
      .map((value) => String(value ?? '').trim())
      .filter(Boolean)
      .join(', ');
  }

  const selectedOptions = (Array.isArray(answerIds) ? answerIds : [answerIds])
    .map((responseId) => {
      const option = (question?.options || []).find((opt) => opt.id === responseId);
      return option
        ? (typeof option.text === 'string' ? option.text : option.text?.text)
        : null;
    })
    .filter(Boolean);

  return selectedOptions.join(', ');
};

const getColumns = (poll) => {
  const columns = [
    { key: 'Nombre', label: 'Nombre', type: 'base' },
    { key: 'Apellido', label: 'Apellido', type: 'base' },
    { key: 'TipoEdad', label: 'Tipo de edad', type: 'base' },
  ];

  if (poll?.paymentSummary?.enabled) {
    columns.push(
      { key: 'Pago', label: 'Estado de pago', type: 'payment' },
      { key: 'MetodoPago', label: 'Metodo de pago', type: 'payment' },
      { key: 'Pagador', label: 'Pagador', type: 'payment' },
      { key: 'Concepto', label: 'Concepto', type: 'payment' },
      { key: 'Importe', label: 'Importe', type: 'payment' },
    );
  }

  (poll?.questions || []).forEach((question, index) => {
    columns.push({
      key: `question_${question.id ?? index}`,
      label: String(question.text || `Pregunta ${index + 1}`),
      question,
      type: 'question',
    });
  });

  return columns;
};

const buildRow = (user, poll) => {
  const row = {
    Nombre: user.name || user.beneficiaryName || '',
    Apellido: user.surname || user.beneficiarySurname || '',
    TipoEdad: ageTypeLabel(user.ageType ?? user.registration?.ageType),
  };

  if (poll?.paymentSummary?.enabled) {
    row.Pago = paymentStatusLabel(user.paymentStatus);
    row.MetodoPago = paymentMethodLabel(user.paymentMethod);
    row.Pagador = `${user.payerSurname || ''} ${user.payerName || ''}`.trim();
    row.Concepto = paymentConceptLabel(user, poll.paymentSummary?.conceptLabel || '-');
    row.Importe = formatCurrency(user.amountCents);
  }

  (poll?.questions || []).forEach((question, index) => {
    const response = user.responses?.find((item) => item.questionId === question.id)?.response || [];
    row[`question_${question.id ?? index}`] = getAnswerText(question, response);
  });

  return row;
};

const buildRows = async (users, poll) => {
  const rows = [];

  for (let index = 0; index < users.length; index += 1) {
    rows.push(buildRow(users[index], poll));

    if ((index + 1) % YIELD_EVERY_ROWS === 0) {
      await yieldToBrowser();
    }
  }

  return rows;
};

const getColumnWidth = (label, values) => {
  const maxLength = values.reduce((acc, value) => {
    const lineLength = String(value ?? '')
      .split('\n')
      .reduce((lineMax, line) => Math.max(lineMax, line.length), 0);
    return Math.max(acc, lineLength);
  }, String(label || '').length);

  return Math.max(12, Math.min(42, maxLength + 2));
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const exportSurveyToExcel = async (users, poll) => {
  const columns = getColumns(poll);
  const rows = await buildRows(users, poll);
  const title = String(poll?.title || 'Resultados de encuesta');
  const generatedAt = new Date().toLocaleString('es-ES');
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(EXCEL_SHEET_NAME, {
    views: [{ state: 'frozen', ySplit: 4 }],
  });

  worksheet.mergeCells(1, 1, 1, columns.length);
  worksheet.getCell(1, 1).value = title;
  worksheet.getCell(1, 1).font = { bold: true, size: 16 };
  worksheet.getCell(1, 1).alignment = { vertical: 'middle' };

  worksheet.mergeCells(2, 1, 2, columns.length);
  worksheet.getCell(2, 1).value = `Generado el ${generatedAt}`;
  worksheet.getCell(2, 1).font = { italic: true, size: 10, color: { argb: 'FF666666' } };

  const headerRow = worksheet.getRow(4);
  columns.forEach((column, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = column.label;
    cell.font = { bold: true, color: { argb: 'FF1F1F1F' } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFEDEDED' },
    };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFD0D0D0' } },
      left: { style: 'thin', color: { argb: 'FFD0D0D0' } },
      bottom: { style: 'thin', color: { argb: 'FFD0D0D0' } },
      right: { style: 'thin', color: { argb: 'FFD0D0D0' } },
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  });
  headerRow.height = 24;

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex];
    const excelRow = worksheet.getRow(5 + rowIndex);
    columns.forEach((column, columnIndex) => {
      const cell = excelRow.getCell(columnIndex + 1);
      cell.value = row[column.key] || '';
      cell.alignment = { vertical: 'top', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFEAEAEA' } },
        left: { style: 'thin', color: { argb: 'FFEAEAEA' } },
        bottom: { style: 'thin', color: { argb: 'FFEAEAEA' } },
        right: { style: 'thin', color: { argb: 'FFEAEAEA' } },
      };
    });
    excelRow.height = 20;

    if ((rowIndex + 1) % YIELD_EVERY_ROWS === 0) {
      // dejamos respirar el hilo principal en exportaciones grandes
      // eslint-disable-next-line no-await-in-loop
      await yieldToBrowser();
    }
  }

  worksheet.columns = columns.map((column) => ({
    key: column.key,
    width: getColumnWidth(
      column.label,
      rows.map((row) => row[column.key] || ''),
    ),
  }));
  worksheet.autoFilter = {
    from: { row: 4, column: 1 },
    to: { row: 4, column: columns.length },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob(
    [buffer],
    {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
  );
  downloadBlob(blob, getSurveyFilename(poll, 'xlsx'));
};

const chunkQuestionColumns = (columns, availableWidth) => {
  const fixedColumns = columns.filter((column) => column.type !== 'question');
  const questionColumns = columns.filter((column) => column.type === 'question');
  const fixedWidth = fixedColumns.reduce((sum, column) => sum + getPdfCellWidth(column.label), 0);
  const targetWidth = Math.max(availableWidth - fixedWidth, PDF_MIN_CELL_WIDTH * 2);
  const chunks = [];
  let currentChunk = [];
  let currentWidth = 0;

  questionColumns.forEach((column) => {
    const width = getPdfCellWidth(column.label);
    if (currentChunk.length > 0 && currentWidth + width > targetWidth) {
      chunks.push(currentChunk);
      currentChunk = [];
      currentWidth = 0;
    }
    currentChunk.push(column);
    currentWidth += width;
  });

  if (currentChunk.length > 0) {
    chunks.push(currentChunk);
  }

  return chunks.length ? chunks : [[]];
};

const getPdfCellWidth = (label) => {
  const base = Math.max(PDF_MIN_CELL_WIDTH, Math.min(PDF_MAX_CELL_WIDTH, String(label || '').length * 4.2));
  return base;
};

const fitTextToCell = (doc, text, width, maxLines) => {
  const safeText = String(text ?? '').trim();
  if (!safeText) return { text: '', overflow: false };

  const wrapped = doc.splitTextToSize(safeText, Math.max(24, width - 6));
  if (wrapped.length <= maxLines) {
    return { text: wrapped, overflow: false };
  }

  const shortened = wrapped.slice(0, maxLines);
  const lastLine = shortened[maxLines - 1] || '';
  shortened[maxLines - 1] = `${lastLine.slice(0, Math.max(0, lastLine.length - 1))}…`;
  return { text: shortened, overflow: true };
};

export const exportSurveyToPdf = async (users, poll) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: PDF_PAGE_FORMAT, compress: true });
  const columns = getColumns(poll);
  const rows = await buildRows(users, poll);
  const availableWidth = doc.internal.pageSize.getWidth() - PDF_MARGINS.left - PDF_MARGINS.right;
  const baseColumns = columns.filter((column) => column.type !== 'question');
  const questionChunks = chunkQuestionColumns(columns, availableWidth);
  const annexRows = [];
  let isFirstTable = true;

  for (const [chunkIndex, questionChunk] of questionChunks.entries()) {
    const pageColumns = [...baseColumns, ...questionChunk];
    for (let rowStart = 0; rowStart < rows.length; rowStart += PDF_ROWS_PER_TABLE) {
      const rowSlice = rows.slice(rowStart, rowStart + PDF_ROWS_PER_TABLE);
      const body = rowSlice.map((row) =>
        pageColumns.map((column) => row[column.key] || ''),
      );

      if (!isFirstTable) {
        doc.addPage(PDF_PAGE_FORMAT, 'landscape');
      }
      isFirstTable = false;

      doc.setFontSize(12);
      doc.text(String(poll?.title || 'Resultados de encuesta'), PDF_MARGINS.left, 32);
      doc.setFontSize(9);
      doc.text(
        `Bloque ${chunkIndex + 1} de ${questionChunks.length} · Registros ${rowStart + 1}-${rowStart + rowSlice.length} de ${rows.length}`,
        PDF_MARGINS.left,
        46,
      );

      autoTable(doc, {
        startY: PDF_MARGINS.top,
        head: [pageColumns.map((column) => column.label)],
        body,
        margin: PDF_MARGINS,
        rowPageBreak: 'avoid',
        theme: 'grid',
        styles: {
          fontSize: PDF_FONT_SIZE,
          cellPadding: 3,
          overflow: 'linebreak',
          valign: 'top',
          lineWidth: 0.1,
        },
        headStyles: {
          fontSize: PDF_HEADER_FONT_SIZE,
          fillColor: [242, 242, 242],
          textColor: [20, 20, 20],
          halign: 'center',
          valign: 'middle',
        },
        columnStyles: pageColumns.reduce((acc, column, index) => {
          acc[index] = {
            cellWidth: getPdfCellWidth(column.label),
            minCellHeight: 18,
          };
          return acc;
        }, {}),
        didParseCell: (hook) => {
          const width = Number(hook.cell.styles.cellWidth || PDF_MIN_CELL_WIDTH);

          if (hook.section === 'head') {
            const { text } = fitTextToCell(doc, hook.cell.raw, width, PDF_MAX_HEADER_LINES);
            hook.cell.text = text;
            return;
          }

          if (hook.section !== 'body') return;

          const raw = Array.isArray(hook.cell.raw) ? hook.cell.raw.join(', ') : hook.cell.raw;
          const { text, overflow } = fitTextToCell(doc, raw, width, PDF_MAX_BODY_LINES);

          if (overflow) {
            const sourceRow = rowSlice[hook.row.index] || {};
            annexRows.push({
              nombre: sourceRow.Nombre || '',
              apellido: sourceRow.Apellido || '',
              campo: pageColumns[hook.column.index]?.label || '',
              texto: String(raw || ''),
            });
            hook.cell.text = ['Ver anexo'];
            return;
          }

          hook.cell.text = text;
        },
        didDrawPage: ({ settings }) => {
          const pageNumber = doc.getCurrentPageInfo().pageNumber;
          doc.setFontSize(8);
          doc.text(
            `Pagina ${pageNumber}`,
            settings.margin.left,
            doc.internal.pageSize.getHeight() - 10,
          );
        },
      });

      // eslint-disable-next-line no-await-in-loop
      await yieldToBrowser();
    }
  }

  if (annexRows.length > 0) {
    doc.addPage(PDF_PAGE_FORMAT, 'landscape');
    doc.setFontSize(12);
    doc.text('Anexo de respuestas largas', PDF_MARGINS.left, 32);

    autoTable(doc, {
      startY: PDF_MARGINS.top,
      head: [['Nombre', 'Apellido', 'Campo', 'Texto completo']],
      body: annexRows.map((row) => [row.nombre, row.apellido, row.campo, row.texto]),
      margin: PDF_MARGINS,
      rowPageBreak: 'avoid',
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: 'linebreak',
        valign: 'top',
      },
      headStyles: {
        fillColor: [242, 242, 242],
        textColor: [20, 20, 20],
      },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: 100 },
        2: { cellWidth: 160 },
        3: { cellWidth: 'auto' },
      },
    });
  }

  doc.save(getSurveyFilename(poll, 'pdf'));
};
